import React, { useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import {
  Box,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Typography,
  TextField,
  Button,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  KeyboardDoubleArrowRight as DoubleArrowRightIcon,
  KeyboardDoubleArrowLeft as DoubleArrowLeftIcon,
  Search as SearchIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
} from '@mui/icons-material';

const TransferList = ({ availableItems, selectedItems, onSelectionChange, title }) => {
  const { t } = useTranslation();
  const displayTitle = title ?? t('pensums.assignCursosToGrade');
  const [leftSelected, setLeftSelected] = useState([]);
  const [rightSelected, setRightSelected] = useState([]);
  const [leftSearch, setLeftSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');

  const filteredLeft = availableItems.filter(item =>
    !selectedItems.some(selected => selected.id === item.id) &&
    item.name?.toLowerCase().includes(leftSearch.toLowerCase())
  );

  const filteredRight = selectedItems.filter(item =>
    item.name?.toLowerCase().includes(rightSearch.toLowerCase())
  );

  const handleLeftSelect = (id) => {
    setLeftSelected(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleRightSelect = (id) => {
    setRightSelected(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleMoveRight = () => {
    const itemsToMove = availableItems.filter(item => 
      leftSelected.includes(item.id) && !selectedItems.some(selected => selected.id === item.id)
    );
    onSelectionChange([...selectedItems, ...itemsToMove]);
    setLeftSelected([]);
  };

  const handleMoveLeft = () => {
    const newSelected = selectedItems.filter(item => !rightSelected.includes(item.id));
    onSelectionChange(newSelected);
    setRightSelected([]);
  };

  const handleMoveAllRight = () => {
    const itemsToMove = availableItems.filter(item => 
      !selectedItems.some(selected => selected.id === item.id)
    );
    onSelectionChange([...selectedItems, ...itemsToMove]);
    setLeftSelected([]);
  };

  const handleMoveAllLeft = () => {
    onSelectionChange([]);
    setRightSelected([]);
  };

  const handleMoveUp = (index) => {
    if (index > 0) {
      const newItems = [...selectedItems];
      [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
      onSelectionChange(newItems);
    }
  };

  const handleMoveDown = (index) => {
    if (index < selectedItems.length - 1) {
      const newItems = [...selectedItems];
      [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
      onSelectionChange(newItems);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader 
        title={displayTitle}
        subheader={t('pensums.selectCursosToAssign')}
      />
      <CardContent>
        <Grid container spacing={3}>
          {/* Available Items (Left) */}
          <Grid item xs={12} md={5}>
            <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {t('pensums.availableCursos')}
                  </Typography>
                  <Chip 
                    label={filteredLeft.length} 
                    color="primary" 
                    size="small"
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t('pensums.searchCursos')}
                  value={leftSearch}
                  onChange={(e) => setLeftSearch(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                  sx={{ mb: 2 }}
                />
                <Paper
                  variant="outlined"
                  sx={{
                    flexGrow: 1,
                    height: 450,
                    overflow: 'auto',
                    backgroundColor: 'grey.50',
                    border: '2px solid',
                    borderColor: 'primary.light',
                  }}
                >
                  <List dense>
                    {filteredLeft.length === 0 ? (
                      <ListItem>
                        <ListItemText
                          primary={t('pensums.noCursosAvailable')}
                          primaryTypographyProps={{ 
                            color: 'text.secondary', 
                            align: 'center',
                            fontStyle: 'italic'
                          }}
                        />
                      </ListItem>
                    ) : (
                      filteredLeft.map((item) => (
                        <ListItem
                          key={item.id}
                          disablePadding
                          secondaryAction={
                            <Checkbox
                              edge="end"
                              checked={leftSelected.includes(item.id)}
                              onChange={() => handleLeftSelect(item.id)}
                            />
                          }
                        >
                          <ListItemButton
                            selected={leftSelected.includes(item.id)}
                            onClick={() => handleLeftSelect(item.id)}
                            sx={{
                              borderRadius: 1,
                              mb: 0.5,
                              mx: 0.5,
                              '&.Mui-selected': {
                                backgroundColor: 'primary.light',
                                color: 'primary.contrastText',
                                '&:hover': {
                                  backgroundColor: 'primary.main',
                                },
                              },
                            }}
                          >
                            <ListItemText
                              primary={item.name}
                              primaryTypographyProps={{
                                fontWeight: leftSelected.includes(item.id) ? 600 : 400,
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))
                    )}
                  </List>
                </Paper>
              </CardContent>
            </Card>
          </Grid>

          {/* Transfer Buttons */}
          <Grid item xs={12} md={2}>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              height="100%"
              gap={1.5}
              sx={{ minHeight: 450 }}
            >
              <Button
                variant="contained"
                onClick={handleMoveRight}
                disabled={leftSelected.length === 0}
                startIcon={<ChevronRightIcon />}
                fullWidth
                size="medium"
                sx={{ minWidth: 120 }}
              >
                {t('pensums.add')}
              </Button>
              <Button
                variant="contained"
                onClick={handleMoveAllRight}
                disabled={filteredLeft.length === 0}
                startIcon={<DoubleArrowRightIcon />}
                fullWidth
                size="medium"
                sx={{ minWidth: 120 }}
              >
                {t('pensums.addAll')}
              </Button>
              <Divider sx={{ my: 1, width: '100%' }} />
              <Button
                variant="outlined"
                onClick={handleMoveLeft}
                disabled={rightSelected.length === 0}
                startIcon={<ChevronLeftIcon />}
                fullWidth
                size="medium"
                color="error"
                sx={{ minWidth: 120 }}
              >
                {t('pensums.remove')}
              </Button>
              <Button
                variant="outlined"
                onClick={handleMoveAllLeft}
                disabled={selectedItems.length === 0}
                startIcon={<DoubleArrowLeftIcon />}
                fullWidth
                size="medium"
                color="error"
                sx={{ minWidth: 120 }}
              >
                {t('pensums.removeAll')}
              </Button>
            </Box>
          </Grid>

          {/* Selected Items (Right) */}
          <Grid item xs={12} md={5}>
            <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" color="success.main" fontWeight="bold">
                    {t('pensums.selectedCursos')}
                  </Typography>
                  <Chip 
                    label={filteredRight.length} 
                    color="success" 
                    size="small"
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t('pensums.searchSelected')}
                  value={rightSearch}
                  onChange={(e) => setRightSearch(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                  sx={{ mb: 2 }}
                />
                <Paper
                  variant="outlined"
                  sx={{
                    flexGrow: 1,
                    height: 450,
                    overflow: 'auto',
                    backgroundColor: 'success.light',
                    opacity: 0.5,
                    border: '2px solid',
                    borderColor: 'success.main',
                    '& .MuiListItemText-primary': {
                      color: '#2E5937',
                    },
                  }}
                >
                  <List dense>
                    {filteredRight.length === 0 ? (
                      <ListItem>
                        <ListItemText
                          primary={t('pensums.noCursosSelected')}
                          primaryTypographyProps={{ 
                            color: 'text.secondary', 
                            align: 'center',
                            fontStyle: 'italic'
                          }}
                        />
                      </ListItem>
                    ) : (
                      filteredRight.map((item, index) => {
                        const originalIndex = selectedItems.findIndex(selected => selected.id === item.id);
                        return (
                          <ListItem
                            key={item.id}
                            
                            secondaryAction={
                              <Box>
                                <IconButton
                                  edge="end"
                                  size="small"
                                  onClick={() => handleMoveUp(originalIndex)}
                                  disabled={originalIndex === 0}
                                  sx={{ mr: 0.5 }}
                                >
                                  <ArrowUpIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  edge="end"
                                  size="small"
                                  onClick={() => handleMoveDown(originalIndex)}
                                  disabled={originalIndex === selectedItems.length - 1}
                                >
                                  <ArrowDownIcon fontSize="small" />
                                </IconButton>
                                <Checkbox
                                  edge="end"
                                  checked={rightSelected.includes(item.id)}
                                  onChange={() => handleRightSelect(item.id)}
                                  sx={{ ml: 1 }}
                                />
                              </Box>
                            }
                          >
                            <ListItemButton
                              selected={rightSelected.includes(item.id)}
                              onClick={() => handleRightSelect(item.id)}
                              sx={{
                                borderRadius: 1,
                                mb: 0.5,
                                mx: 0.5,
                                pr: 12, // Add padding-right to make room for arrows and checkbox
                                color: '#2E5937',
                                '&.Mui-selected': {
                                  backgroundColor: 'success.light',
                                  color: '#2E5937',
                                  '&:hover': {
                                    backgroundColor: 'success.main',
                                    color: 'white',
                                  },
                                },
                                '&:hover': {
                                  backgroundColor: 'rgba(46, 89, 55, 0.1)',
                                },
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Box display="flex" alignItems="center" gap={1} sx={{ pr: 1 }}>
                                    <Chip 
                                      label={originalIndex + 1} 
                                      size="small" 
                                      color="success"
                                      sx={{ minWidth: 30, fontWeight: 'bold' }}
                                    />
                                    <Typography 
                                      variant="body1" 
                                      fontWeight={rightSelected.includes(item.id) ? 600 : 500}
                                      sx={{ 
                                        color: '#2E5937',
                                        fontSize: '0.95rem',
                                        flex: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {item.name}
                                    </Typography>
                                  </Box>
                                }
                                primaryTypographyProps={{
                                  sx: { color: '#2E5937' }
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })
                    )}
                  </List>
                </Paper>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default TransferList;
